// @flow
import Immutable from 'seamless-immutable';
import axios from 'axios';
import moment from 'moment';
import { get, isPlainObject } from 'lodash';
import Bugsnag from '@bugsnag/js';
import ClinicService from '../services/ClinicService';
import { userActions } from './modules/context/user';
import { clinicActions } from './modules/context/clinics';
import { Log } from '../helpers';
import { clearLocalLogin } from '../helpers/localLogout';
import type { State } from '../types';

// The most a try waits for the user and the clinics: a dead connection must fail some time. app.js says so
// on screen sooner (START_SLOW_AFTER) and keeps waiting: a slow answer still opens the app.
export const START_REQUEST_TIMEOUT = 90 * 1000;
// The longest a Retry-After from the API is followed
const MAX_RETRY_AFTER = 10 * 60 * 1000;

// What the start is waiting for, for app.js (StartStalled's and StartUnreachable's metadata): the requests
// still on their way
export type StartProgress = (step: string, pending?: Array<string>) => void;

type StartOptions = {
  // Cancels the requests, where AbortController exists
  signal?: ?AbortSignal,
  // Whether app.js gave up on this try ("Sair e entrar de novo", a new connection), checked after every wait
  isCancelled?: () => boolean,
  onProgress?: StartProgress,
};

// The stored state as read once at the start: ready to use, or (a new version) needing the user and clinics
type StoredStart = { state: Object, needsContext?: void } | { state?: void, needsContext: Object };

const status = (error: any): ?number => get(error, ['response', 'status']);

// Something between the app and the API answered: a body that is neither the API's JSON (an object once
// axios parsed it) nor empty. The API's errors are JSON or empty (Rails' head :unauthorized, :not_found,
// :forbidden); CloudFront's and a WAF's are HTML pages.
const isMiddleboxAnswer = (response: Object): boolean => {
  const data = get(response, 'data');
  if (isPlainObject(data) || data === undefined || data === null) return false;
  return String(data).trim() !== '';
};

// No answer (offline, a dropped connection, a laptop waking up, the timeout, the request cut off), the API
// or CloudFront failing (408, 429, 5xx), or a middlebox answering: another try may work. A 2xx only fails
// here when its body wasn't the API's JSON (fetchContext's answered): empty or not, that's not the API
// either. A 401 is the API saying the login expired. Any other answer is the API refusing (a deleted
// account's token is a 404), and a code error is a bug: trying again would get the same.
const isUnreachableError = (error: any): boolean => {
  if (!axios.isAxiosError(error)) return false;
  if (!error.response) return true;
  const code = status(error);
  if (code === 401) return false;
  if (code >= 200 && code < 300) return true;
  return code === 408 || code === 429 || code >= 500 || isMiddleboxAnswer(error.response);
};

// users#show answers this when the stored clinic is one the user left (or none was sent)
const isWrongClinic = (error: any): boolean => status(error) === 400 && get(error, ['response', 'data', 'code']) === 'wrong_clinic';

// The API's Retry-After (a 429 or a 503), in ms: seconds or an HTTP date
const retryAfterMs = (error: any): ?number => {
  const value = get(error, ['response', 'headers', 'retry-after']);
  if (value === undefined || value === null || value === '') return null;
  const seconds = Number(value);
  const ms = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(String(value)) - Date.now();
  return Number.isFinite(ms) && ms > 0 ? Math.min(ms, MAX_RETRY_AFTER) : null;
};

// The start couldn't reach the API for the new version's user and clinics. Nothing was changed: the
// stored state, login included, is as it was, and app.js offers "Tentar de novo".
export const isStartUnreachable = (error: any): boolean => Boolean(error && error.startUnreachable);
// app.js gave up on the try: whatever it would have answered doesn't count
const isStartCancelled = (error: any): boolean => Boolean(error && error.startCancelled);
const cancelledError = () => Object.assign(new Error('The start gave up on this try'), { startCancelled: true });

const loggedOutState = (initialState: ?State) => ({
  ...initialState || {},
  appVersion: Immutable({}),
  context: Immutable({}),
  auth: Immutable({}),
  ui: Immutable({}),
});

// "Sair e entrar de novo" on the can't-reach screen: a logged-out start without the API (it can't be reached),
// with logout()'s local cleanup. The stored login goes now, so a closed tab doesn't bring it back.
export const signedOutStart = (initialState: ?State) => {
  clearLocalLogin();
  return loggedOutState(initialState);
};

// The stored state, read once per start (a hung read is the stall watchdog's): as it is, or, after a new
// version, waiting for a fresh user and clinics. Rejects when it can't be read (app.js offers "Recarregar").
export const readStoredState = async (initialState: ?State): Promise<StoredStart> => {
  const storedState = await window.idb.fetchInitialState();
  if (!storedState) return { state: loggedOutState(initialState) };

  const storedStateVersion = get(storedState, ['appVersion', 'version'], '');
  const isStoredStateValid = !storedStateVersion || (storedStateVersion === window.APP_VERSION);
  try {
    if (isStoredStateValid) return { state: Immutable(storedState) };
  } catch (error) {
    Log.error('Start: the stored state could not be used', error && error.message);
    Bugsnag.notify(error, (event) => { event.context = 'StartStoredStateFailed'; });
    return { state: loggedOutState(initialState) };
  }
  // Logged out already: nothing to fetch, and no network needed to show the login
  if (!get(storedState, ['auth', 'token'])) return { state: loggedOutState(initialState) };
  return { needsContext: storedState };
};

// Settles when every request has, or at the first 401: the login expired, so the other one is canceled
const settleUnlessUnauthorized = (requests: Array<Promise<any>>, cancelOthers: () => void) => new Promise((resolve, reject) => {
  const results = [];
  let left = requests.length;
  requests.forEach((request, i) => {
    request.then((value) => { results[i] = { status: 'fulfilled', value }; }, (reason) => {
      results[i] = { status: 'rejected', reason };
      if (status(reason) === 401) {
        cancelOthers();
        reject(reason);
      }
    }).then(() => {
      left -= 1;
      if (!left) resolve(results);
    });
  });
});

// A new version starts from a fresh user and clinics (the old version's context and ui are dropped, so
// the new code never reads a shape it doesn't know). Both at once: neither needs the other.
const fetchContext = async (storedState: Object, { signal, isCancelled, onProgress }: Object) => {
  const token = get(storedState, ['auth', 'token'], null);
  const clinicId = get(storedState, ['context', 'clinics', 'activeClinic', 'id'], null);
  axios.defaults.headers.clinicId = clinicId;
  axios.defaults.headers['access-token'] = token;

  // Its own controller, following the start's: a 401 on one request cancels the other
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  if (controller && signal) signal.addEventListener('abort', () => controller.abort());
  const options = { signal: controller ? controller.signal : undefined, timeout: START_REQUEST_TIMEOUT };

  const pending = ['user', 'clinics'];
  // Each answer must be the API's: a 200 from something else (an HTML page) would leave an empty context
  const answered = (name) => (response) => {
    if (pending.includes(name)) pending.splice(pending.indexOf(name), 1);
    onProgress('context', [...pending]);
    if (!isPlainObject(response.data)) {
      throw new axios.AxiosError('Not the API\'s answer', axios.AxiosError.ERR_BAD_RESPONSE, response.config, response.request, response);
    }
    return response;
  };
  onProgress('context', [...pending]);

  const getUserAction = userActions.getUser();
  const getClinicsAction = clinicActions.getClinics(null, clinicId);
  const results = await settleUnlessUnauthorized([
    getUserAction.call(undefined, options).then(answered('user')),
    getClinicsAction.call(undefined, options).then(answered('clinics')),
  ], () => { if (controller) controller.abort(); });
  if (isCancelled()) throw cancelledError();

  // The stored clinic is one the user left (or none): the user again, for the clinic the clinics' answer
  // made active. Without any clinic (a registration not finished) /user can't answer: the stored user
  // stands in, as the login's does until a clinic exists.
  const clinicsAnswer = get(results, [1, 'value', 'data']);
  if (results[0].status === 'rejected' && isWrongClinic(results[0].reason) && clinicsAnswer) {
    const activeClinicId = get(clinicsAnswer, ['clinic', 'id']);
    if (activeClinicId) {
      axios.defaults.headers.clinicId = activeClinicId;
      results[0] = await getUserAction.call(undefined, { ...options, headers: { clinicId: activeClinicId } })
        .then(answered('user'))
        .then((value) => ({ status: 'fulfilled', value }), (reason) => ({ status: 'rejected', reason }));
      if (isCancelled()) throw cancelledError();
    } else if (clinicsAnswer.userWithoutClinic) {
      delete axios.defaults.headers.clinicId; // the clinic the user left
      results[0] = { status: 'fulfilled', value: { data: { user: get(storedState, ['context', 'user']) || {} } } };
    }
  }

  // A 401 settled it already; one that couldn't reach the API means trying again; otherwise the API
  // refused or the code broke
  const failures = results.filter((result) => result.status === 'rejected').map((result) => result.reason);
  if (failures.length) {
    const unauthorized = failures.find((error) => status(error) === 401);
    if (unauthorized) throw unauthorized;
    const unreachable = failures.find(isUnreachableError);
    if (unreachable) {
      throw Object.assign(unreachable, {
        startUnreachable: true,
        retryAfterMs: Math.max(0, ...failures.map(retryAfterMs).filter(Boolean)) || null,
      });
    }
    throw failures[0];
  }

  const [{ value: getUserResponse }, { value: getClinicsResponse }] = (results: any);
  getUserAction.parseResponse(getUserResponse.data); // the user's language, as on login
  const userData = getUserResponse.data.user || {};
  const { activeClinic, multiClinics, userWithoutClinic } = getClinicsAction.parseResponse(getClinicsResponse.data);
  const normalizedActiveClinic = ClinicService.normalizeActiveClinic(activeClinic);
  const normalizedMulticlinics = ClinicService.normalizeMulticlinics(multiClinics);
  const lastFetch = moment().toISOString();

  return {
    user: {
      ...userData,
      lastFetch,
    },
    clinics: {
      activeClinic: normalizedActiveClinic.activeClinic,
      data: normalizedMulticlinics,
      userWithoutClinic: userWithoutClinic || false,
      doctors: {
        data: normalizedActiveClinic.doctors,
      },
      secretaries: {
        data: normalizedActiveClinic.secretaries,
        managerIds: normalizedActiveClinic.secretaryManagerIds,
        nonManagerIds: normalizedActiveClinic.secretaryNoManagerIds,
        lastFetch,
      },
      lastFetch,
    },
  };
};

// One try at a new version's state: the stored one with a fresh user and clinics. Only this is tried again.
// Rejects when the API can't be reached (isStartUnreachable: app.js offers "Tentar de novo") and when app.js
// gave up on the try. Only the API's answer logs out: a 401 (an expired token), as before; any other
// refusal or a bug too, and Bugsnag hears of it. A logout clears what logout() clears on the device.
export const withFreshContext = async (
  storedState: Object,
  initialState: ?State,
  { signal, isCancelled = () => false, onProgress = () => {} }: StartOptions = {},
): Promise<any> => {
  const cancelled = () => Boolean((signal && signal.aborted) || isCancelled());
  try {
    const context = await fetchContext(storedState, { signal, isCancelled: cancelled, onProgress });
    if (cancelled()) throw cancelledError();
    return Immutable({
      ...storedState,
      appVersion: {
        version: window.APP_VERSION,
        storeReview: get(storedState, ['appVersion', 'storeReview'], false),
      },
      context,
      ui: {},
    });
  } catch (error) {
    if (cancelled() || isStartCancelled(error)) throw cancelledError();
    if (isStartUnreachable(error)) throw error;
    const answer = status(error) || null;
    Log.error('Start: the new version could not load the user and clinics', answer, error && error.message);
    if (answer !== 401) {
      Bugsnag.notify(error, (event) => {
        event.context = 'StartRefetchFailed';
        event.addMetadata('start', { step: 'context', status: answer });
      });
    }
  }

  clearLocalLogin();
  return loggedOutState(initialState);
};
